<template>
  <div class="eligible-campaigns-wrapper" v-if="eligibleCampaigns.length > 0">
    <div class="eligible-campaigns-card">
      <div class="card-header">
        <h3 class="card-title">
          {{ $t("dashboard.eligible_campaigns_title", "Campaigns eligible to go live") }}
        </h3>
      </div>

      <div class="card-content">
        <div
          v-for="campaign in eligibleCampaigns"
          :key="campaign.campaign_id"
          class="campaign-item"
          :class="{ submitting: submittingCampaigns[campaign.campaign_id] }"
        >
          <div class="campaign-info-compact">
            <div class="campaign-id-compact">
              {{ $t("dashboard.eligible_campaigns_campaign_id", "Campaign ID") }}:
              {{ campaign.campaign_id }}
            </div>
            <div class="campaign-name-compact">{{ campaign.campaign.campaigntitle }}</div>
            <div class="campaign-meta-compact">
              {{ formatCategory(campaign.campaign.category) }} •
              {{ formatCurrency(campaign.campaign.currency) }} •
              {{ $t("dashboard.eligible_campaigns_goal", "Goal") }}:
              {{ formatGoal(campaign.campaign) }}
            </div>
          </div>

          <!-- Payment warning -->
          <div
            v-if="!campaign.fee_data.campaign_paid || !campaign.fee_data.has_active_payment_option"
            class="campaign-warning"
          >
            <!-- Case 1: Payment not done, but payment option exists -->
            <span
              v-if="!campaign.fee_data.campaign_paid && campaign.fee_data.has_active_payment_option"
            >
              {{ $t("dashboard.eligible_campaigns_payment_warning", "Please") }}
              <a :href="campaign.fee_data.link_to_pay" class="campaign-link" target="_blank">
                {{ $t("dashboard.eligible_campaigns_pay_here", "pay here") }}
              </a>
              {{
                $t("dashboard.eligible_campaigns_payment_complete", "for your campaign to go live.")
              }}
            </span>

            <!-- Case 2: Payment not done AND no active payment option -->
            <span
              v-else-if="
                !campaign.fee_data.campaign_paid && !campaign.fee_data.has_active_payment_option
              "
            >
              {{ $t("dashboard.eligible_campaigns_payment_warning", "Please") }}
              <a :href="campaign.fee_data.link_to_pay" class="campaign-link" target="_blank">
                {{ $t("dashboard.eligible_campaigns_pay_here", "pay here") }}
              </a>
              {{
                $t(
                  "dashboard.eligible_campaigns_payment_middle",
                  "for your campaign and ensure you have an"
                )
              }}
              <router-link to="/gateways" class="campaign-link">
                {{ $t("dashboard.eligible_campaigns_warning_payment", "active payment option") }}
              </router-link>
              {{
                $t(
                  "dashboard.eligible_campaigns_warning_suffix",
                  "before submitting your live request."
                )
              }}
            </span>

            <!-- Case 3: Payment done but no active payment option -->
            <span
              v-else-if="
                campaign.fee_data.campaign_paid && !campaign.fee_data.has_active_payment_option
              "
            >
              {{ $t("dashboard.eligible_campaigns_ensure_payment", "Ensure you have an") }}
              <router-link to="/gateways" class="campaign-link">
                {{ $t("dashboard.eligible_campaigns_warning_payment", "active payment option") }}
              </router-link>
              {{
                $t(
                  "dashboard.eligible_campaigns_warning_suffix",
                  "before submitting your live request."
                )
              }}
            </span>
          </div>

          <!-- Error display -->
          <div v-if="campaignErrors[campaign.campaign_id]" class="error-message">
            {{ campaignErrors[campaign.campaign_id] }}
          </div>

          <div v-if="campaign.fee_data.passed_validation" class="campaign-actions-compact">
            <input
              v-model="campaignMessages[campaign.campaign_id]"
              type="text"
              class="message-input-compact"
              :placeholder="
                $t(
                  'dashboard.eligible_campaigns_message_placeholder',
                  'Enter your message for the live request...'
                )
              "
              :disabled="submittingCampaigns[campaign.campaign_id]"
              @keyup.enter="submitRequest(campaign)"
            />
            <button
              class="submit-btn-compact"
              @click.prevent="submitRequest(campaign)"
              :disabled="
                submittingCampaigns[campaign.campaign_id] ||
                !campaignMessages[campaign.campaign_id]?.trim()
              "
            >
              {{
                submittingCampaigns[campaign.campaign_id]
                  ? $t("dashboard.submitting", "Submitting...")
                  : $t("dashboard.eligible_campaigns_submit_btn", "Submit")
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";

export default defineComponent({
  name: "EligibleCampaignsCard",

  props: {
    eligibleCampaigns: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      campaignMessages: {},
      submittingCampaigns: {},
      campaignErrors: {},
    };
  },

  methods: {
    async submitRequest(campaign) {
      const campaignId = campaign.campaign_id;
      const message = this.campaignMessages[campaignId];

      if (!message || !message.trim() || this.submittingCampaigns[campaignId]) {
        return;
      }

      this.$set(this.submittingCampaigns, campaignId, true);
      this.$delete(this.campaignErrors, campaignId); // Clear previous errors

      try {
        const success = await this.$store.dispatch("submitLiveRequest", {
          campaignId,
          message: message.trim(),
        });

        if (success) {
          // Remove the campaign from eligible list
          this.$store.commit("removeEligibleCampaign", campaignId);
          // Clear the message for this campaign
          this.$delete(this.campaignMessages, campaignId);
          this.$delete(this.submittingCampaigns, campaignId);
          this.$delete(this.campaignErrors, campaignId);

          // Show success message (optional)
          // You can add a toast notification here if you have one
        }
      } catch (error) {
        console.error("Failed to submit live request:", error);
        this.$set(this.campaignErrors, campaignId, this.parseErrorMessage(error));
        this.$set(this.submittingCampaigns, campaignId, false);
      }
    },

    parseErrorMessage(error) {
      try {
        // Handle structured error response like the one provided
        if (error && error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
          // Return the first error message from the errors array
          return (
            error.errors[0].message ||
            this.$t("dashboard.generic_error", "An error occurred. Please try again.")
          );
        }

        // Handle simple error message
        if (error && error.message) {
          return error.message;
        }

        // Handle string error
        if (typeof error === "string") {
          return error;
        }

        // Fallback to generic error
        return this.$t("dashboard.generic_error", "An error occurred. Please try again.");
      } catch (parseError) {
        console.error("Error parsing error message:", parseError);
        return this.$t("dashboard.generic_error", "An error occurred. Please try again.");
      }
    },

    formatCategory(category) {
      const categoryMap = {
        regular: this.$t("dashboard.category_regular", "Regular"),
        givingday: this.$t("dashboard.category_givingday", "Giving Day"),
        directdonation: this.$t("dashboard.category_directdonation", "Direct Donation"),
      };
      return categoryMap[category] || category;
    },

    formatCurrency(currency) {
      return (currency || "").toUpperCase();
    },

    formatGoal(campaign) {
      const goal = campaign.lookingprice || 0;
      const currency = this.formatCurrency(campaign.currency);
      return `${currency} ${goal.toLocaleString()}`;
    },

    formatDate(dateString) {
      if (!dateString) return "";

      try {
        const date = new Date(dateString);
        return date.toLocaleDateString();
      } catch (error) {
        return dateString;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.eligible-campaigns-wrapper {
  margin-bottom: 24px;
}

.eligible-campaigns-card {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border-left: 4px solid #28a745;
}

.card-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);

  .card-title {
    margin: 0 0 8px 0;
    font-size: 20px;
    font-weight: 700;
    color: #1b5e20;
  }

  .card-subtitle {
    margin: 0;
    font-size: 14px;
    color: #2e7d32;
    line-height: 1.5;
  }
}

.card-content {
  padding: 20px;
}

.campaign-item {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border: none;
  border-left: 4px solid #28a745;
  border-radius: 6px;
  padding: 16px 20px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    box-shadow: 0 2px 8px rgba(40, 167, 69, 0.2);
  }

  &.submitting {
    opacity: 0.7;
    pointer-events: none;
  }
}

.campaign-info-compact {
  margin-bottom: 12px;

  .campaign-id-compact {
    font-size: 13px;
    font-weight: 600;
    color: #1b5e20;
    margin-bottom: 6px;
  }

  .campaign-name-compact {
    font-size: 16px;
    font-weight: 700;
    color: #212529;
    margin-bottom: 6px;
  }

  .campaign-meta-compact {
    font-size: 13px;
    color: #2e7d32;
    font-weight: 500;
  }
}

.campaign-warning {
  background: #fff3cd;
  color: #856404;
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  border: 1px solid #ffeeba;
  font-size: 16px;
  font-weight: 500;
}

.error-message {
  background: #f8d7da;
  color: #721c24;
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  border: 1px solid #f5c6cb;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.validation-warning {
  background: #fff3cd;
  color: #856404;
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  border: 1px solid #ffeeba;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  text-align: center;
}

.campaign-link {
  color: #856404;
  text-decoration: underline;
  margin: 0 4px;
}

.campaign-link code {
  color: inherit;
  background: transparent;
  padding: 0;
  border: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, "Roboto Mono", "Courier New", monospace;
}

.campaign-actions-compact {
  display: flex;
  gap: 8px;
  align-items: center;

  .message-input-compact {
    flex: 1;
    padding: 10px 14px;
    border: 2px solid #28a745;
    border-radius: 6px;
    font-size: 14px;
    background: #ffffff;
    transition: all 0.2s ease;

    &::placeholder {
      color: #adb5bd;
    }

    &:focus {
      outline: none;
      border-color: #1e7e34;
      box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.15);
    }

    &:disabled {
      background-color: #f8f9fa;
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  .submit-btn-compact {
    padding: 10px 20px;
    background: #28a745;
    color: #ffffff;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
      background: #1e7e34;
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(40, 167, 69, 0.3);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.25);
    }
  }
}

@media (max-width: 768px) {
  .card-header {
    padding: 16px 20px;

    .card-title {
      font-size: 18px;
    }

    .card-subtitle {
      font-size: 13px;
    }
  }

  .card-content {
    padding: 16px;
  }

  .campaign-item {
    padding: 16px;
  }

  .campaign-title {
    h4 {
      font-size: 16px;
    }
  }

  .campaign-actions {
    padding: 16px;

    button {
      width: 100%;
      align-self: stretch;
    }
  }
}
</style>
